// Client-side validation for the commission-rate effective-date fields.
//
// Mirrors the server DTO (`CreateCommissionRateDto` — @IsDateString +
// EffectiveFromBeforeTo) so a malformed date or an end-before-start range is
// caught inline in the Add/Edit modal instead of round-tripping to the API and
// surfacing as a generic "Failed" toast. No React imports — pure functions so
// the logic is unit-testable in the admin (node) vitest env.

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** True only for a real calendar date in strict `YYYY-MM-DD` form. */
export function isValidIsoDate(value: string): boolean {
  if (!ISO_DATE_RE.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  // Reject roll-overs the regex can't catch (e.g. 2026-02-30, 2026-13-40).
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export interface DateFieldErrors {
  effective_from?: string;
  effective_to?: string;
}

/**
 * Validate the effective-from / effective-to pair as entered in the modal.
 * Returns a per-field error map (empty when valid). `requireFrom` is true when
 * creating (the start date is mandatory); in edit mode the start date is
 * immutable and already valid, so only the format of `to` and the from ≤ to
 * range are checked.
 */
export function validateEffectiveDates(
  from: string,
  to: string,
  opts: { requireFrom: boolean },
): DateFieldErrors {
  const errors: DateFieldErrors = {};

  if (opts.requireFrom && !from) {
    errors.effective_from = 'Effective From is required';
  } else if (from && !isValidIsoDate(from)) {
    errors.effective_from = 'Enter a valid date (YYYY-MM-DD)';
  }

  if (to && !isValidIsoDate(to)) {
    errors.effective_to = 'Enter a valid date (YYYY-MM-DD)';
  }

  // from ≤ to only when both are well-formed dates (lexicographic compare is
  // correct for zero-padded YYYY-MM-DD).
  if (!errors.effective_from && !errors.effective_to && from && to && from > to) {
    errors.effective_to = 'Effective To must be on or after Effective From';
  }

  return errors;
}
